import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import { camelizeKeys } from 'humps';
import getSplashApiClient from 'lib/SplashApiClient/singleton';
import { getInstance as getBootstrap } from 'utils/bootstrap';

/**
 * Form Renderer v3 is the react form.
 * This is the API client that it uses.
 */
export default class FormRendererV3ApiClient {
    /**
     * @param {SplashApiClient} apiClient
     */
    constructor(apiClient) {
        this.apiClient = apiClient;
    }

    /**
     *
     * @param {string} eventId
     */
    getRemainigResponses(eventId) {
        return this.apiClient
            .get(`/form-submission/response/limits/event/${eventId}`)
            .then(this.formatRemainigResponsesAsValueToAvailability);
    }

    /**
     *
     * @param  {Object} response - eg:
     * { data: {
     *      limits: [
     *          { node_id: 169, abcq_id: 4, value: "One", available: 19 },
     *          ...
     *      ],
     * }}
     * @return {Object} - response limits grouped by question id. Camelized keys.
     */
    formatRemainigResponsesAsValueToAvailability(response) {
        const limits = get(response, 'data.limits', []).filter((descriptor) => descriptor.available !== undefined);

        return camelizeKeys(groupBy(limits, 'node_id'));
    }

    /**
     * @param {string} eventSubdomain
     * @return {Promise}
     */
    getForm(eventSubdomain) {
        const bootstrap = getBootstrap();
        const ticketHash = bootstrap.get('ticketHash', null);

        return this.apiClient
            .get(
                `/public/event/${eventSubdomain}/registration-form`,
                {},
                {
                    omitAccessToken: true,
                    headers: {
                        'X-Splash-Private-Hash': get(window, 'splash.server.constants.ticketHash') ?? ticketHash,
                    },
                },
            )
            .then(({ data: { form } }) => form);
    }

    /**
     * @param {string} eventId
     * @return {Promise}
     */
    getFormByEventId(eventId, formId = null) {
        const bootstrap = getBootstrap();
        const ticketHash = bootstrap.get('ticketHash', null);
        const segments = bootstrap.get('segments', null);

        const params = Array.isArray(segments) && segments.length > 0 ? { segments } : {};

        return this.apiClient
            .get(`/public/event/${eventId}/form${formId ? `/${formId}` : ''}`, params, {
                omitAccessToken: true,
                headers: {
                    'X-Splash-Private-Hash': get(window, 'splash.server.constants.ticketHash') ?? ticketHash,
                },
            })
            .then(({ data: { form } }) => form);
    }

    /**
     * @param {string} formId
     * @return {Promise}
     */
    getFormById(formId) {
        return this.apiClient
            .get(`/public/form/${formId}/preview`, {}, { omitAccessToken: true })
            .then(({ data: { form } }) => form);
    }

    getSubmission(eventSubdomain, token) {
        return getSplashApiClient()
            .get(`event/${eventSubdomain}/rsvp-form/response?access_token=${token}`, {}, { omitAccessToken: true })
            .then(({ data: { submission } }) => submission);
    }

    getTicketSaleForm(ticketId) {
        return this.apiClient.get(`/public/forms/ticket-type/${ticketId}`, {}, { omitAccessToken: true });
    }

    /**
     * Get sessions for the event.
     * @param {string} eventId - The ID of the event
     * @return {Promise<Array>} - Promise resolving to array of sessions
     */
    getSessions(eventId) {
        return this.apiClient
            .get(`/public/events/${eventId}/sessions`, { source: 'registration_form' })
            .then(({ data: sessions }) => sessions);
    }
}

export const getFormRendererV3ApiClient = () => new FormRendererV3ApiClient(getSplashApiClient());
